import { useEffect, useState } from "react";
import { cachedRequest } from "./api";

// The backend is the single source of truth for CVI scores. These helpers only read
// /api/cvi results; they never calculate, estimate or default a score.
//  - /api/cvi/periods/:periodId/scope        → people the viewer may see (hierarchy-authorized),
//                                               final snapshots for locked periods.
//  - /api/cvi/periods/:periodId/users/:userId → one person's full result and breakdown.
// A null score means "not available" (no data yet, request failed, or not authorized) and is
// shown as "—", never as 0.

function viewerKey() {
    try { return JSON.parse(sessionStorage.getItem("cvi_user") || "null")?.id || ""; } catch { return ""; }
}

export function cviValue(value) {
    if (value == null || value === "") return null;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
}

export function formatScore(value, digits = 0) {
    const numeric = cviValue(value);
    return numeric == null ? "—" : numeric.toFixed(digits);
}

// Mean of the available server values only; missing results are reported, not counted as zero.
export function averageScore(values) {
    const available = values.map(cviValue).filter(value => value != null);
    return { value: available.length ? available.reduce((sum, value) => sum + value, 0) / available.length : null, count: available.length, total: values.length };
}

export function scopeScoresByUser(payload) {
    const map = new Map();
    for (const row of payload?.results || []) {
        if (row?.user?.id) map.set(row.user.id, row.scores || null);
    }
    return map;
}

// Every member in a local unit has the same authoritative KPI-only score.
// Reject inconsistent values rather than inventing a cross-unit aggregation rule.
export function consistentTeamKpiScore(members, viewer, kpis) {
    const sameUnit = (members || []).filter(member =>
        viewer?.orgUnitId ? member.orgUnitId === viewer.orgUnitId : member.teamId === viewer?.teamId
    );
    const values = sameUnit.map(member => cviValue(kpis.get(member.id))).filter(score => score != null);
    if (!values.length || values.some(score => Math.abs(score - values[0]) > 0.01)) return null;
    return values[0];
}

export function scopeKpisByUser(payload) {
    const map = new Map();
    for (const row of payload?.results || []) {
        if (row?.user?.id) map.set(row.user.id, cviValue(row.teamKpiScore));
    }
    return map;
}

// Strategic indicators of the people in scope, with the engine's achievement (formula template,
// cap and approved-data rule applied). A null achievement means "no approved data", never 0%.
export function scopeIndicatorsById(payload) {
    const map = new Map();
    for (const indicator of payload?.indicators || []) {
        if (indicator?.indicatorId) map.set(String(indicator.indicatorId), { ...indicator, achievement: cviValue(indicator.achievement) });
    }
    return map;
}

export function useScopedCvi(periodId) {
    const [state, setState] = useState({ scores: new Map(), kpis: new Map(), indicators: new Map(), loaded: !periodId, error: null, source: null });
    useEffect(() => {
        let cancelled = false;
        if (!periodId) { setState({ scores: new Map(), kpis: new Map(), indicators: new Map(), loaded: true, error: null, source: null }); return undefined; }
        setState(prev => ({ ...prev, scores: new Map(), kpis: new Map(), indicators: new Map(), loaded: false, error: null, source: null }));
        const path = `/api/cvi/periods/${encodeURIComponent(periodId)}/scope`;
        cachedRequest(path, { key: `${path}|${viewerKey()}` })
            .then(payload => { if (!cancelled) setState({ scores: scopeScoresByUser(payload), kpis: scopeKpisByUser(payload), indicators: scopeIndicatorsById(payload), loaded: true, error: null, source: payload?.source || null }); })
            .catch(error => { if (!cancelled) setState({ scores: new Map(), kpis: new Map(), indicators: new Map(), loaded: true, error, source: null }); });
        return () => { cancelled = true; };
    }, [periodId]);
    const scoresFor = userId => state.scores.get(userId) || null;
    const indicatorFor = indicatorId => state.indicators.get(String(indicatorId)) || null;
    return { ...state, scoresFor, indicatorFor };
}

export function useMemberCvi(userId, periodId) {
    const [state, setState] = useState({ data: null, loaded: false, error: null });
    useEffect(() => {
        let cancelled = false;
        if (!userId || !periodId) { setState({ data: null, loaded: true, error: null }); return undefined; }
        setState({ data: null, loaded: false, error: null });
        const path = `/api/cvi/periods/${encodeURIComponent(periodId)}/users/${encodeURIComponent(userId)}`;
        cachedRequest(path, { key: `${path}|${viewerKey()}` })
            .then(data => { if (!cancelled) setState({ data, loaded: true, error: null }); })
            .catch(error => { if (!cancelled) setState({ data: null, loaded: true, error }); });
        return () => { cancelled = true; };
    }, [userId, periodId]);
    return state;
}
